import { useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { setNewlyCreatedCustomer } from '../../../../store/slices/customerSelectionSlice';
import { initialFormData } from '../constants/initialStates';

const useCustomerForm = ({ addCustomer, updateCustomer, deleteCustomer, toast, th, dealerId, siteUseId }) => {
    const dispatch = useDispatch();
    const [formData, setFormData] = useState(initialFormData);
    const [originalFormData, setOriginalFormData] = useState(initialFormData);
    const [errors, setErrors] = useState({});
    const [dialogVisible, setDialogVisible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [filters, setFilters] = useState({});
    const [pagination, setPagination] = useState({
        PageNumber: 1,
        PageSize: 10
    });
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const customerSchema = Yup.object().shape({
        firstName: Yup.string().trim().required(th('customerDefinition.validation.firstNameRequired')),
        lastName: Yup.string()
            .trim()
            .when('customerType', {
                is: (val) => val === '2',
                then: (schema) => schema.nullable(),
                otherwise: (schema) => schema.required(th('customerDefinition.validation.lastNameRequired'))
            }),
        tcNumber: Yup.number()
            .transform((value, originalValue) => {
                if (originalValue === '' || originalValue === null || originalValue === undefined) {
                    return null;
                }
                return value;
            })
            .when(['country', 'customerType'], {
                is: (country, customerType) => (country?.text === 'Türkiye' || country?.name === 'Türkiye') && customerType !== '2',
                then: (schema) =>
                    schema
                        .required(th('customerDefinition.validation.tcNumberRequired'))
                        .test('len', th('customerDefinition.validation.tcNumberLength'), (val) => val && val.toString().length === 11),
                otherwise: (schema) => schema.nullable()
            }),
        phone: Yup.number()
            .transform((value, originalValue) => {
                if (originalValue === '' || originalValue === null || originalValue === undefined) {
                    return null;
                }
                return value;
            })
            .required(th('customerDefinition.validation.phoneRequired')),
        address: Yup.string().trim().required(th('customerDefinition.validation.addressRequired')),
        country: Yup.mixed()
            .test('country-required', th('customerDefinition.validation.countryRequired'), (value) => {
                if (!value) return false;
                if (typeof value === 'object') {
                    return !!(value.text || value.name);
                }
                return false;
            })
            .required(th('customerDefinition.validation.countryRequired')),
        city: Yup.string().trim().max(100).nullable(),
        postalCode: Yup.number()
            .transform((value, originalValue) => {
                if (originalValue === '' || originalValue === null || originalValue === undefined) {
                    return null;
                }
                return value;
            })
            .nullable(),
        // Çoklu şasi listesi validasyonu
        chassisList: Yup.array()
            .of(
                Yup.object().shape({
                    chassisNo: Yup.string().required(),
                    licensePlate: Yup.string().nullable()
                })
            )
            .when('customerType', {
                is: (val) => val === '2', // Tüzel kişi için zorunlu değil
                then: (schema) => schema.nullable(),
                otherwise: (schema) => schema.min(1, th('customerDefinition.validation.chassisListRequired'))
            }),
        customerType: Yup.string().nullable(),
        taxOffice: Yup.string()
            .trim()
            .when('customerType', {
                is: (val) => val === '2',
                then: (schema) => schema.required(th('customerDefinition.validation.taxOfficeRequired')),
                otherwise: (schema) => schema.nullable()
            }),
        taxNumber: Yup.number()
            .transform((value, originalValue) => {
                if (originalValue === '' || originalValue === null || originalValue === undefined) {
                    return null;
                }
                return value;
            })
            .when('customerType', {
                is: (val) => val === '2',
                then: (schema) => schema.required(th('customerDefinition.validation.taxNumberRequired')),
                otherwise: (schema) => schema.nullable()
            }),
        partsDiscountRate: Yup.number()
            .transform((value, originalValue) => {
                if (originalValue === '' || originalValue === null || originalValue === undefined) {
                    return null;
                }
                return value;
            })
            .nullable(),
        laborDiscountRate: Yup.number()
            .transform((value, originalValue) => {
                if (originalValue === '' || originalValue === null || originalValue === undefined) {
                    return null;
                }
                return value;
            })
            .nullable()
    });

    const checkForChanges = (currentData, originalData) => {
        const isEqual = (val1, val2) => {
            if (val1 == null && val2 == null) return true;
            if (val1 === '' && val2 == null) return true;
            if (val1 == null && val2 === '') return true;
            if (typeof val1 === 'object' && typeof val2 === 'object') {
                return JSON.stringify(val1) === JSON.stringify(val2);
            }
            return val1 === val2;
        };

        const hasChanges = !Object.keys(currentData).every((key) => {
            if (key === 'id') return true;
            return isEqual(currentData[key], originalData[key]);
        });

        setHasUnsavedChanges(hasChanges);
        return hasChanges;
    };

    const updateFormData = (newData) => {
        setFormData(newData);
        checkForChanges(newData, originalFormData);
    };

    const handleNew = () => {
        const freshData = {
            ...initialFormData,
            customerType: '1',
            taxOffice: '',
            taxNumber: null,
            partsDiscountRate: null,
            laborDiscountRate: null,
            chassisList: [],
            chassis: '',
            selectedPlate: ''
        };
        setFormData(freshData);
        setOriginalFormData(freshData);
        setEditMode(false);
        setDialogVisible(true);
        setHasUnsavedChanges(false);
        setErrors({});
    };

    const handleEdit = (customer) => {
        const countryObject = customer.country
            ? {
                  id: customer.country === 'Türkiye' ? 'TR' : customer.country,
                  code: customer.country === 'Türkiye' ? 'TR' : customer.country,
                  text: customer.country,
                  name: customer.country,
                  displayValue: customer.country
              }
            : null;

        // API'den gelen chassis array'ini yükle
        // Eğer chassis array varsa onu kullan, yoksa geriye dönük uyumluluk için eski alanlardan oluştur
        let chassisList = [];
        if (Array.isArray(customer.chassis) && customer.chassis.length > 0) {
            chassisList = customer.chassis.map(item => ({
                id: item.id,
                chassisNo: item.chassisNo,
                licensePlate: item.licensePlate || ''
            }));
        } else if (customer.chassisNo) {
            // Geriye dönük uyumluluk: eski tek şasi alanından liste oluştur
            chassisList = [{
                chassisNo: customer.chassisNo,
                licensePlate: customer.licensePlate || ''
            }];
        }

        const editData = {
            id: customer.id,
            firstName: customer.name,
            lastName: customer.surname,
            tcNumber: customer.identityNo,
            phone: customer.phoneNumber,
            address: customer.address,
            country: countryObject,
            city: customer.city || '',
            postalCode: customer.zipCode,
            chassisList: chassisList,
            chassis: '',
            selectedPlate: '',
            customerType: customer.customerType ? String(customer.customerType) : '1',
            taxOffice: customer.taxOffice || '',
            taxNumber: customer.taxNumber ?? null,
            partsDiscountRate: customer.partsDiscountRate ?? null,
            laborDiscountRate: customer.laborDiscountRate ?? null
        };

        setFormData(editData);
        setOriginalFormData(editData);
        setEditMode(true);
        setDialogVisible(true);
        setHasUnsavedChanges(false);
        setErrors({});
    };

    const handleSave = async () => {
        try {
            await customerSchema.validate(formData, {
                abortEarly: false
            });
            setErrors({});

            const countryText = formData.country?.text || formData.country?.name || '';

            // Ortak veri yapısı
            const customerPayload = {
                firstName: formData.firstName || '',
                lastName: formData.lastName || '',
                tcNumber: formData.tcNumber || '',
                phone: formData.phone || '',
                address: formData.address || '',
                country: countryText,
                city: formData.city || '',
                postalCode: formData.postalCode || '',
                chassisList: formData.chassisList || [],
                customerType: formData.customerType || '1',
                taxOffice: formData.taxOffice || null,
                taxNumber: formData.taxNumber !== undefined && formData.taxNumber !== null ? String(formData.taxNumber) : null,
                partsDiscountRate:
                    formData.partsDiscountRate !== undefined && formData.partsDiscountRate !== null
                        ? Number(formData.partsDiscountRate)
                        : null,
                laborDiscountRate:
                    formData.laborDiscountRate !== undefined && formData.laborDiscountRate !== null
                        ? Number(formData.laborDiscountRate)
                        : null
            };

            if (editMode) {
                await updateCustomer({
                    id: formData.id,
                    ...customerPayload
                }).unwrap();

                toast.current.show({
                    severity: 'success',
                    summary: th('common.success'),
                    detail: th('customerDefinition.updateSuccess'),
                    life: 3000
                });
            } else {
                const response = await addCustomer({
                    ...customerPayload,
                    bosBranchCode: String(dealerId || ''),
                    bosBranchSiteCode: String(siteUseId || '')
                }).unwrap();

                if (response?.id) {
                    const customerData = {
                        id: response.id,
                        name: response.name,
                        surname: response.surname,
                        phoneNumber: formData.phone
                    };
                    dispatch(setNewlyCreatedCustomer(customerData));
                }

                toast.current.show({
                    severity: 'success',
                    summary: th('common.success'),
                    detail: th('customerDefinition.addSuccess'),
                    life: 3000
                });
            }

            setDialogVisible(false);
            setHasUnsavedChanges(false);
            return true;
        } catch (error) {
            if (error.inner) {
                const newErrors = {};
                error.inner.forEach((err) => {
                    newErrors[err.path] = err.message;
                });
                setErrors(newErrors);
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: th('common.error'),
                    detail: error?.data?.message || th('customerDefinition.saveError'),
                    life: 3000
                });
            }
            return false;
        }
    };

    const handleDelete = async () => {
        try {
            if (formData.id) {
                await deleteCustomer(formData.id).unwrap();
                toast.current.show({
                    severity: 'success',
                    summary: th('common.success'),
                    detail: th('customerDefinition.deleteSuccess'),
                    life: 3000
                });
                setDialogVisible(false);
                setHasUnsavedChanges(false);
                return true;
            }
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: th('common.error'),
                detail: error?.data?.message || th('customerDefinition.deleteError'),
                life: 3000
            });
            return false;
        }
    };

    const handleClose = (forceClose = false) => {
        if (!forceClose && hasUnsavedChanges) {
            return false;
        }
        setDialogVisible(false);
        setErrors({});
        setHasUnsavedChanges(false);
        return true;
    };

    const handleApplyFilters = (updatedFilters) => {
        const newFilters = {};
        Object.entries(updatedFilters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                const trimmedValue = typeof value === 'string' ? value.trim() : value;
                if (trimmedValue !== '') {
                    newFilters[key] = trimmedValue;
                }
            }
        });
        setFilters(newFilters);
        setPagination({ ...pagination, PageNumber: 1 });
    };

    const handleResetFilters = () => {
        setFilters({});
        setPagination({ ...pagination, PageNumber: 1 });
    };

    const handlePageChange = (newPagination) => {
        setPagination(newPagination);
    };

    // Şasi ekleme fonksiyonu
    const addChassis = (chassisNo, licensePlate) => {
        if (!chassisNo) return { success: false, message: th('customerDefinition.validation.chassisRequired') };

        // Duplicate kontrolü
        const isDuplicate = formData.chassisList?.some(
            item => item.chassisNo === chassisNo
        );

        if (isDuplicate) {
            return { success: false, message: th('customerDefinition.duplicateChassis') };
        }

        const newChassis = {
            chassisNo: chassisNo,
            licensePlate: licensePlate || ''
        };

        const updatedList = [...(formData.chassisList || []), newChassis];
        const updatedFormData = {
            ...formData,
            chassisList: updatedList,
            chassis: '',
            selectedPlate: ''
        };

        updateFormData(updatedFormData);
        return { success: true };
    };

    // Şasi silme fonksiyonu
    const removeChassis = (chassisNo) => {
        const updatedList = formData.chassisList?.filter(
            item => item.chassisNo !== chassisNo
        ) || [];

        const updatedFormData = {
            ...formData,
            chassisList: updatedList
        };

        updateFormData(updatedFormData);
    };

    return {
        formData,
        setFormData: updateFormData,
        errors,
        dialogVisible,
        editMode,
        filters,
        pagination,
        hasUnsavedChanges,
        handleNew,
        handleEdit,
        handleSave,
        handleDelete,
        handleClose,
        handleApplyFilters,
        handleResetFilters,
        handlePageChange,
        addChassis,
        removeChassis
    };
};

export default useCustomerForm;
